import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Licence } from 'src/app/models/license';
import { LicenseFiles } from 'src/app/models/license-files';
import { LocationService } from 'src/app/services/location.service';
import { Countries } from 'src/app/utils/location/countries';
import { NgxSpinnerService } from 'ngx-spinner';
import { MessageUtil } from 'src/app/utils/message.util';
import { CustomerService } from 'src/app/services/customer.service';
import { FileUpload } from 'primeng/fileupload';

@Component({
  selector: 'app-license-form',
  templateUrl: './license-form.component.html',
  styleUrls: ['./license-form.component.scss']
})
export class LicenseFormComponent implements OnInit, OnChanges {
  @ViewChild('upload', { static: false }) upload: FileUpload;
  @Input() customerLicense: Licence;
  
  licenseForm: FormGroup;
  submitted = false;
  isBrazilianLicense = true;
  
  licenseTypes = [
    { label: 'Selecione...', value: null },
    { label: 'Veleiro', value: 'SAILBOAT' },
    { label: 'Motonauta', value: 'MOTONAUTA' },
    { label: 'Arrais-Amador', value: 'ARRAIS_AMATEUR' },
    { label: 'Mestre-Amador', value: 'MASTER_AMATEUR' },
    { label: 'Capitão-Amador', value: 'AMATEUR_CAPTAIN' },
    { label: 'Arrais-Amador e Motonauta', value: 'ARRAIS_AMATEUR_MOTONAUTA' },
    { label: 'Mestre-Amador e Motonauta', value: 'MASTER_AMATEUR_MOTONAUTA' },
    { label: 'Capitão-Amador e Motonauta', value: 'AMATEUR_CAPTAIN_MOTONAUTA' },{ label: 'Carta de Marinheiro', value: 'MARINER_CARD' },
    { label: 'Carta de Patrão Local', value: 'LOCAL_PATROL_CARD' },
    { label: 'Carta de Patrão de Costa', value: 'COAST_PATROL_CARD' },
    { label: 'Carta de Patrão de Alto Mar', value: 'HIGH_SEA_PATROL_CARD' }
  ];
  countries = Countries.countryList.map(c => {
    const countryNamesPt = {
      'Brazil': 'Brasil',
      'United States': 'Estados Unidos',
    };
    
    const label = countryNamesPt[c.name] || c.name;
    return { label, value: c.code };
  });
  states: any[] = [];
  filteredCities: string[] = [];

  public fileUploadUrl: string = '';

  constructor(
    private fb: FormBuilder,
    private locationService: LocationService,
    private messageService: MessageService,
    public config: DynamicDialogConfig,
    public dialogRef: DynamicDialogRef,
    private customerService: CustomerService,
    private spinner: NgxSpinnerService,
    private messageUtil: MessageUtil
  ) { }

  ngOnInit(): void {
    this.initializeForm();
    this.loadStates('BR');
    
    if (this.config && this.config.data && this.config.data.customer) {
      console.log('Dados do cliente recebidos:', this.config.data.customer);
      
      this.customerLicense = this.config.data.customer.licence;
      
      if (this.customerLicense) {
        console.log('Licença encontrada:', this.customerLicense);
        this.populateForm();
      } else {
        console.log('Cliente não possui licença cadastrada');
      }
    }
  }
  
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.customerLicense && this.customerLicense && this.licenseForm) {
      this.populateForm();
    }
  }

  initializeForm(): void {
    this.licenseForm = this.fb.group({
      register: ['', Validators.required],
      expiration: [null, Validators.required],
      typeLicense: [null, Validators.required],
      country: ['BR', Validators.required],
      state: [null, Validators.required],
      city: [null, Validators.required],
      fileLicense: [null],
      idLicense: [null],
      fileUrl: [null]
    });
    
    this.licenseForm.get('country').valueChanges.subscribe(country => {
      this.isBrazilianLicense = country === 'BR';
      this.loadStates(country);
    });
    
    this.licenseForm.get('state').valueChanges.subscribe(state => {
      if (state) {
        this.loadCities();
      }
    });
  }
  
  populateForm(): void {
    if (!this.customerLicense) return;
    
    if (this.customerLicense.file) {
      const fileName = this.customerLicense.file.split('/').pop();
      this.licenseForm.patchValue({
        fileLicense: {
          name: fileName,
          path: this.customerLicense.file
        },
        fileUrl: this.customerLicense.file
      });
      this.fileUploadUrl = this.customerLicense.file;
      console.log('URL do arquivo principal:', this.fileUploadUrl);
      
    }
    
    if (this.customerLicense.files?.length > 0) {
      const file = this.customerLicense.files[0];
      this.licenseForm.patchValue({
        fileLicense: {
          id: file.id,
          name: file.name,
          path: file.path
        },
        idLicense: file.id,
        fileUrl: file.path
      });
      this.fileUploadUrl = file.path;
    }
    
    const country = this.customerLicense.country || 'BR';
    this.isBrazilianLicense = country === 'BR';
    
    this.loadStates(country);
    
    this.licenseForm.patchValue({
      register: this.customerLicense.register,
      expiration: this.customerLicense.expiration ? new Date(this.customerLicense.expiration) : null,
      typeLicense: this.customerLicense.typeLicense,
      country: country,
      state: this.customerLicense.state,
      city: this.customerLicense.city
    });
    
    if (this.customerLicense.state) {
      this.loadCities();
    }
    
    if (this.customerLicense.files && this.customerLicense.files.length > 0) {
      console.log('Arquivos da licença encontrados:', this.customerLicense.files);
      
      while (this.files.length) {
        this.files.removeAt(0);
      }
      
      this.customerLicense.files.forEach(file => {
        this.files.push(this.fb.group({
          id: [file.id],
          name: [file.name],
          path: [file.path]
        }));
      });
    } else {
      console.log('Nenhum arquivo de licença encontrado');
    }
  }

  get files() {
    return this.licenseForm.get('files') as FormArray;
  }

  onCountryChange(): void {
  }

  loadStates(country: string): void {
    this.states = this.locationService.getStates()
      .map(s => ({ label: s.name, value: s.id }));
  }

  loadCities(): void {
    const country = this.licenseForm.get('country').value;
    const state = this.licenseForm.get('state').value;
    
    if (country && state) {
      this.locationService.getCities(country, state).subscribe(cities => {
        this.filteredCities = cities.map(c => c.name);
      });
    }
  }

  get filePreview() {
    return this.licenseForm.value.fileLicense;
  }

  filterCities(event: any): void {
    if (this.filteredCities.length === 0) {
      this.loadCities();
      return;
    }
    
    this.filteredCities = this.filteredCities.filter(c => 
      c.toLowerCase().includes(event.query.toLowerCase())
    );
  }

  async onFileSelect(files): Promise<any> {
    if (!files || files.length === 0) return;
    
    this.spinner.show();
    try {
      const response = await this.customerService.uploadLicence(files).toPromise();
      console.log('Resposta do upload completa:', response);
      
      this.fileUploadUrl = response.url;
      const fileName = response.name || files[0].name;
      

      if (!this.fileUploadUrl) {
        console.warn('URL não encontrada diretamente na resposta. Tentando outros métodos...');
        
        if (response?.data?.attachment?.url) {
          this.fileUploadUrl = response.data.attachment.url;
          console.log('URL encontrada em data.attachment:', this.fileUploadUrl);
        }
        else {
          const responseStr = JSON.stringify(response);
          const urlMatch = responseStr.match(/(https:\/\/s3-sa-east-1\.amazonaws\.com\/[^"]+)/);
          if (urlMatch) {
            this.fileUploadUrl = urlMatch[0];
            console.log('URL extraída do JSON:', this.fileUploadUrl);
          }
        }
      }
      
      if (!this.fileUploadUrl) {
        console.error('ERRO: Não foi possível encontrar a URL em nenhum lugar da resposta:', response);
      }
      
      this.licenseForm.patchValue({
        fileLicense: {
          name: fileName,
          path: this.fileUploadUrl
        },
        fileUrl: this.fileUploadUrl
      });
      
      this.messageUtil.generateMessage(
        'success',
        'Sucesso',
        'Arquivo anexado com sucesso.'
      );
      
      this.upload?.clear();
    } catch (error) {
      console.error('Erro ao processar arquivo:', error);
      this.upload?.clear();
    } finally {
      this.spinner.hide();
    }
  }

    onUploadFinish(url: string): void {
      if (!url) return;
      this.fileUploadUrl = url;
    
    const urlParts = url.split('/');
    const fileName = urlParts[urlParts.length - 1];
    
    this.licenseForm.patchValue({
      fileLicense: {
        name: fileName,
        path: url
      },
      fileUrl: url
    });
    
    
  }

  addFile(f?: LicenseFiles): void {
    if (!f) {
      f = new LicenseFiles();
    }
    const controls = [
      ...(this.licenseForm.get('licence.files') as UntypedFormArray).controls,
    ];
    const formGroup = new UntypedFormGroup({
      id: new UntypedFormControl(f.id),
      name: new UntypedFormControl(f.name),
      path: new UntypedFormControl(f.path),
    });

    controls.push(formGroup);

    (this.licenseForm.get('licence.files') as UntypedFormArray).push(
      formGroup
    );
  }

  removeFile(): void {
    this.licenseForm.patchValue({
      fileLicense: null,
      idLicense: null,
      fileUrl: null
    });
    this.fileUploadUrl = '';
  }
  
  viewFile(file: any): void {
    let fileUrl = '';
    
    if (file && file.path) {
      fileUrl = file.path;
    } else if (this.fileUploadUrl) {
      fileUrl = this.fileUploadUrl;
    } else if (this.licenseForm.get('fileUrl').value) {
      fileUrl = this.licenseForm.get('fileUrl').value;
    }
    
    if (fileUrl) {
       window.open(fileUrl, '_blank');
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Não foi possível visualizar o arquivo. Caminho não encontrado.'
      });
    }
  }

  async save(): Promise<void> {
    this.submitted = true;
    if (!this.licenseForm.valid) {
      this.messageUtil.generateMessage('warn', 'Atenção', 'Preencha todos os campos obrigatórios');
      return;
    }
    
    const formValue = this.licenseForm.value;
    const customerId = this.config.data.customer.id;
    
    const hasFile = this.fileUploadUrl || formValue.fileUrl || formValue.fileLicense?.path;
    if (!hasFile) {
      this.messageUtil.generateMessage('warn', 'Atenção', 'É obrigatório adicionar uma imagem da licença');
      return;
    }
    
    const payload: any = {
      register: formValue.register,
      expiration: this.formatDate(formValue.expiration),
      typeLicense: formValue.typeLicense,
      country: formValue.country,
      state: formValue.state,
      city: formValue.city
    };
    
    if (this.fileUploadUrl) {
      payload.file = this.fileUploadUrl;
      
    } 
    else if (formValue.fileUrl) {
      payload.file = formValue.fileUrl;
    }
    else if (formValue.fileLicense?.path) {
      payload.file = formValue.fileLicense.path;
    }
    
    
    
    this.spinner.show();
    try {
      if (this.customerLicense?.id) {
        const result = await this.customerService.updateLicense(
          customerId,
          this.customerLicense.id,
          payload
        ).toPromise();
        console.log('Resposta ao salvar licença:', result);
        this.messageUtil.generateMessage('success', 'Sucesso', 'Licença atualizada com sucesso');
      } else {
        const result = await this.customerService.createLicense(
          customerId,
          payload
        ).toPromise();
        console.log('Resposta ao criar licença:', result);
        this.messageUtil.generateMessage('success', 'Sucesso', 'Licença criada com sucesso');
      }
      this.dialogRef.close(true);
    } catch (error) {
      console.error('Erro ao salvar licença:', error);
      const errorMsg = error.error?.message || typeof error.error === 'string' ? error.error : 'Erro ao salvar licença';
      this.messageUtil.generateMessage('error', 'Erro', errorMsg);
    } finally {
      this.spinner.hide();
    }
  }
  
  private formatDate(date: Date): string {
    return date ? date.toISOString().split('T')[0] : '';
  }
}